'use client';

import { useTranslations } from 'next-intl';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import { Link } from '@/i18n/navigation';
import { productGroups, companyLinks, companyLinks2 } from './nav-links';
import { LinkItem } from './nav-link-item';

/**
 * The Product / Company expandable menus that drop out of the pill. Stock
 * shadcn NavigationMenu (Radix) supplies the open/close + morph animation; only
 * the trigger chrome is toned down to match the pill's quiet text links. Lives
 * inside the existing header pill — it does not replace it.
 */

// Quiet trigger on the green bar: cream text that brightens + gets a soft
// knockout wash on hover/open. Every hover/focus/open state is pinned to the
// cream wash so the stock shadcn `bg-accent` (a bright cream) never flashes
// through on the green bar — including the open+hover / open+focus stacks.
const triggerClass =
  'h-auto gap-1 rounded-full bg-transparent px-3 py-2 text-small font-medium text-primary-foreground/75 hover:bg-primary-foreground/10 hover:text-primary-foreground focus:bg-primary-foreground/10 focus:text-primary-foreground data-[state=open]:bg-primary-foreground/10 data-[state=open]:text-primary-foreground data-[state=open]:hover:bg-primary-foreground/10 data-[state=open]:focus:bg-primary-foreground/10';

export function DesktopNav() {
  const t = useTranslations('header.menu');

  return (
    <NavigationMenu className="hidden md:flex">
      <NavigationMenuList className="gap-1">
        {/* Product */}
        <NavigationMenuItem>
          <NavigationMenuTrigger className={triggerClass}>{t('product')}</NavigationMenuTrigger>
          <NavigationMenuContent className="bg-muted/50 p-1 pe-1.5 dark:bg-background">
            <div className="grid w-[34rem] grid-cols-2 gap-4 rounded-lg border bg-popover p-3 shadow">
              {productGroups.map((group) => (
                <div key={group.key} className="flex flex-col gap-1">
                  <span className="px-2 pb-1 text-overline text-muted-foreground">
                    {t(`productGroups.${group.key}`)}
                  </span>
                  {group.items.map((item) => (
                    <NavigationMenuLink
                      asChild
                      key={item.key}
                      className="flex-row items-center gap-x-3 rounded-lg p-2"
                    >
                      <LinkItem
                        href={item.href}
                        icon={item.icon}
                        external={item.external}
                        label={t(`productLinks.${item.key}.label`)}
                        description={t(`productLinks.${item.key}.description`)}
                      />
                    </NavigationMenuLink>
                  ))}
                </div>
              ))}
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>

        {/* Company */}
        <NavigationMenuItem>
          <NavigationMenuTrigger className={triggerClass}>{t('company')}</NavigationMenuTrigger>
          <NavigationMenuContent className="bg-muted/50 p-1 pe-1.5 pb-1.5 dark:bg-background">
            <div className="grid w-[34rem] grid-cols-2 gap-2">
              <div className="space-y-1 rounded-lg border bg-popover p-2 shadow">
                {companyLinks.map((item) => (
                  <NavigationMenuLink
                    asChild
                    key={item.key}
                    className="flex-row items-center gap-x-3 rounded-lg p-2"
                  >
                    <LinkItem
                      href={item.href}
                      icon={item.icon}
                      external={item.external}
                      label={t(`companyLinks.${item.key}.label`)}
                      description={t(`companyLinks.${item.key}.description`)}
                    />
                  </NavigationMenuLink>
                ))}
              </div>
              <div className="space-y-1 p-2">
                {companyLinks2.map((item) => (
                  <NavigationMenuLink
                    asChild
                    key={item.key}
                    className="flex-row items-center gap-x-2"
                  >
                    {item.external ? (
                      <a href={item.href} target="_blank" rel="noopener noreferrer">
                        {item.icon}
                        {t(`companyLinks2.${item.key}`)}
                      </a>
                    ) : (
                      <Link href={item.href}>
                        {item.icon}
                        {t(`companyLinks2.${item.key}`)}
                      </Link>
                    )}
                  </NavigationMenuLink>
                ))}
              </div>
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>
  );
}
